'use client';

import { useId, useMemo, useState, type ReactNode } from 'react';
import { ArrowDown, CircleQuestionMark } from 'lucide-react';
import type { WorkloadMetricsInterval } from '@/lib/urm-api';
import { MetricsLoadingOverlay } from '@/components/metrics-loading-overlay';
import {
  buildApiCostRatioSummaries,
  buildCombinedApiCostRatioSummary,
  type ApiCostRatioSummary,
} from '../cost-ratio.logic';

type CostSortKey = 'avgRatio' | 'minRatio' | 'maxRatio' | 'windowMeanMs' | 'comparedIntervals';

function formatRatio(value: number | null): string {
  if (value === null) return '—';
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}×`;
}

function formatLatency(value: number | null): string {
  if (value === null) return '—';
  return value >= 1_000
    ? `${(value / 1_000).toLocaleString(undefined, { maximumFractionDigits: 2 })} s`
    : `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} ms`;
}

function sortValue(summary: ApiCostRatioSummary, key: CostSortKey): number {
  return summary[key] ?? Number.NEGATIVE_INFINITY;
}

function ColumnHelp({ label, children }: { label: string; children: ReactNode }) {
  const tooltipId = useId();
  return (
    <span className="group relative inline-flex">
      <button
        type="button"
        aria-label={`${label} explanation`}
        aria-describedby={tooltipId}
        className="cursor-help rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <CircleQuestionMark className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      <span
        id={tooltipId}
        role="tooltip"
        className="pointer-events-none invisible absolute top-full right-0 z-50 mt-1.5 w-72 rounded-md border border-slate-700 bg-slate-950 px-2.5 py-2 text-left text-xs font-normal whitespace-normal text-white opacity-0 shadow-lg transition-opacity duration-75 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100"
      >
        {children}
      </span>
    </span>
  );
}

function SortHeader({
  label,
  sortKey,
  selectedSortKey,
  onSelect,
  help,
}: {
  label: string;
  sortKey: CostSortKey;
  selectedSortKey: CostSortKey;
  onSelect: (key: CostSortKey) => void;
  help?: string;
}) {
  const selected = sortKey === selectedSortKey;
  return (
    <th
      scope="col"
      aria-sort={selected ? 'descending' : 'none'}
      className="sticky top-0 z-10 border-b bg-card px-2 py-2 text-right font-medium text-muted-foreground"
    >
      <span className="inline-flex items-center justify-end gap-1">
        <button
          type="button"
          aria-label={`Sort by ${label}`}
          aria-pressed={selected}
          onClick={() => onSelect(sortKey)}
          className="inline-flex items-center gap-0.5 rounded-sm outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
        >
          {label}
          {selected && <ArrowDown className="h-3 w-3" aria-hidden="true" />}
        </button>
        {help && <ColumnHelp label={label}>{help}</ColumnHelp>}
      </span>
    </th>
  );
}

function CostMetricCells({ summary }: { summary: ApiCostRatioSummary }) {
  return (
    <>
      <td className="whitespace-nowrap border-b px-2 py-1.5 text-right font-mono tabular-nums">{formatRatio(summary.avgRatio)}</td>
      <td className="whitespace-nowrap border-b px-2 py-1.5 text-right font-mono tabular-nums">{formatRatio(summary.minRatio)}</td>
      <td className="whitespace-nowrap border-b px-2 py-1.5 text-right font-mono tabular-nums">{formatRatio(summary.maxRatio)}</td>
      <td className="whitespace-nowrap border-b px-2 py-1.5 text-right font-mono tabular-nums">{formatLatency(summary.windowMeanMs)}</td>
      <td className="border-b px-2 py-1.5 text-right font-mono tabular-nums">{summary.comparedIntervals}</td>
    </>
  );
}

export function ApiCostRatioOverview({
  intervals,
  loading,
}: {
  intervals: WorkloadMetricsInterval[];
  loading: boolean;
}) {
  const [sortKey, setSortKey] = useState<CostSortKey>('avgRatio');
  const [selectedRowKeys, setSelectedRowKeys] = useState<Set<string>>(() => new Set());
  const summaries = useMemo(() => buildApiCostRatioSummaries(intervals), [intervals]);
  const baselineMissing = summaries.length > 0 && !summaries[0].baselinePresent;
  const activeSelectedKeys = useMemo(() => {
    const availableKeys = new Set(summaries.map((summary) => summary.key));
    return new Set(Array.from(selectedRowKeys).filter((key) => availableKeys.has(key)));
  }, [selectedRowKeys, summaries]);
  const combinedSummary = useMemo(
    () => buildCombinedApiCostRatioSummary(intervals, activeSelectedKeys),
    [activeSelectedKeys, intervals],
  );
  const sortedSummaries = useMemo(() => [...summaries].sort((left, right) => (
    sortValue(right, sortKey) - sortValue(left, sortKey) || left.label.localeCompare(right.label)
  )), [sortKey, summaries]);
  const allRowsSelected = summaries.length > 0 && activeSelectedKeys.size === summaries.length;
  const toggleRow = (key: string) => {
    setSelectedRowKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  return (
    <section
      aria-label="API cost vs GET"
      className="space-y-2 rounded-xl border border-primary/20 bg-muted/40 p-2.5 shadow-sm"
    >
      <div className="min-w-0 rounded-lg border bg-card px-3 pt-3 pb-2 shadow-sm">
        <div className="mb-2 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <h3 className="text-sm font-semibold">API cost vs GET</h3>
          <p className="text-xs text-muted-foreground">
            Mean latency of each API relative to a simple General · get · resources, compared per interval. Select rows to combine them.
          </p>
        </div>
        <MetricsLoadingOverlay loading={loading}>
          {summaries.length === 0 && !loading ? (
            <div className="p-8 text-center text-sm text-muted-foreground">No workload metrics were recorded in this window.</div>
          ) : (
            <>
              {baselineMissing && (
                <p className="mb-2 text-xs text-amber-700 dark:text-amber-300">
                  General · get · resources had no requests in this window, so every ratio is unavailable.
                </p>
              )}
              <div className="max-h-[720px] overflow-auto">
                <table aria-label="API cost ratio details" className="w-full min-w-[900px] table-fixed border-separate border-spacing-0 text-xs">
                  <colgroup>
                    <col className="w-[4%]" />
                    <col className="w-[14%]" />
                    <col className="w-[8%]" />
                    <col className="w-[14%]" />
                    <col className="w-[11%]" />
                    <col className="w-[11%]" />
                    <col className="w-[11%]" />
                    <col className="w-[13%]" />
                    <col className="w-[14%]" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th scope="col" className="sticky top-0 z-10 border-b bg-card px-2 py-2 text-center font-medium text-muted-foreground">
                        <input
                          type="checkbox"
                          checked={allRowsSelected}
                          aria-label={allRowsSelected ? 'Clear all API row selections' : 'Select all API rows'}
                          title={allRowsSelected ? 'Clear selection' : 'Select all rows'}
                          onChange={() => setSelectedRowKeys(
                            allRowsSelected ? new Set() : new Set(summaries.map((summary) => summary.key)),
                          )}
                          className="h-3.5 w-3.5 accent-primary"
                        />
                      </th>
                      <th scope="col" className="sticky top-0 z-10 border-b bg-card px-2 py-2 text-left font-medium text-muted-foreground">ResourceType</th>
                      <th scope="col" className="sticky top-0 z-10 border-b bg-card px-2 py-2 text-left font-medium text-muted-foreground">Action</th>
                      <th scope="col" className="sticky top-0 z-10 border-b bg-card px-2 py-2 text-left font-medium text-muted-foreground">APILabel</th>
                      <SortHeader
                        label="Avg cost" sortKey="avgRatio" selectedSortKey={sortKey} onSelect={setSortKey}
                        help="How expensive this API is compared to a simple GET: 2× means it typically takes twice as long. Technically: the average, across intervals, of this API's mean latency ÷ the baseline GET's mean latency in the same interval."
                      />
                      <SortHeader
                        label="Low (p10)" sortKey="minRatio" selectedSortKey={sortKey} onSelect={setSortKey}
                        help="The cost on this API's cheaper intervals — close to its best case, ignoring the single cheapest outlier. Technically: the 10th percentile of the per-interval cost ratios."
                      />
                      <SortHeader
                        label="High (p90)" sortKey="maxRatio" selectedSortKey={sortKey} onSelect={setSortKey}
                        help="The cost on this API's more expensive intervals — close to its worst case, without being set by a single very slow outlier. Technically: the 90th percentile of the per-interval cost ratios."
                      />
                      <SortHeader
                        label="Mean latency" sortKey="windowMeanMs" selectedSortKey={sortKey} onSelect={setSortKey}
                        help="How long an average request to this API takes over the whole window. Technically: total processing time ÷ total requests."
                      />
                      <SortHeader
                        label="Compared intervals" sortKey="comparedIntervals" selectedSortKey={sortKey} onSelect={setSortKey}
                        help="How many time slices went into the cost figures. Technically: intervals where both this API and the baseline GET had at least one request; only these contribute to the ratio."
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {combinedSummary && (
                      <tr className="bg-primary/5">
                        <td className="border-b px-2 py-1.5 text-center">
                          <input
                            type="checkbox"
                            checked
                            aria-label="Clear combined selection"
                            title="Clear selection"
                            onChange={() => setSelectedRowKeys(new Set())}
                            className="h-3.5 w-3.5 accent-primary"
                          />
                        </td>
                        <th scope="row" colSpan={3} className="border-b px-2 py-1.5 text-left font-semibold text-primary">
                          {combinedSummary.label}
                        </th>
                        <CostMetricCells summary={combinedSummary} />
                      </tr>
                    )}
                    {sortedSummaries.map((summary) => (
                      <tr key={summary.key} className={activeSelectedKeys.has(summary.key) ? 'bg-primary/[0.03]' : undefined}>
                        <td className="border-b px-2 py-1.5 text-center">
                          <input
                            type="checkbox"
                            checked={activeSelectedKeys.has(summary.key)}
                            aria-label={`Select ${summary.label}`}
                            onChange={() => toggleRow(summary.key)}
                            className="h-3.5 w-3.5 accent-primary"
                          />
                        </td>
                        <td title={summary.resourceType} className="truncate border-b px-2 py-1.5 font-mono">{summary.resourceType}</td>
                        <td title={summary.action} className="truncate border-b px-2 py-1.5 font-mono">{summary.action}</td>
                        <td title={summary.apiLabel} className="truncate border-b px-2 py-1.5 font-mono">{summary.apiLabel}</td>
                        <CostMetricCells summary={summary} />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-2 text-[10px] text-muted-foreground">
                Baseline is General · get · resources. Each ratio compares mean latency within the same interval; low / high are the p10 / p90 across the window, so one outlier interval does not skew them.
              </p>
            </>
          )}
        </MetricsLoadingOverlay>
      </div>
    </section>
  );
}
